import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  IconButton,
  Button,
  Chip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  SelectChangeEvent,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Event as EventIcon,
  FilterListOff as FilterListOffIcon,
} from '@mui/icons-material';
import { joursFeriesAPI } from '../../../services/api';
import {
  JourFerie,
  JourFerieCreate,
  JourFerieUpdate,
  TYPE_JOUR_FERIE_OPTIONS,
} from '../../../types/jourFerie';
import { extractErrorMessage } from '../../../utils/errorHandler';
import useAlert from '../../../hooks/useAlert';
import useFormDraft from '../../../hooks/useFormDraft';
import AlertDialog from '../../../components/AlertDialog';
import { getModuleTableTheme } from '../../../theme/tableTheme';
import CustomTablePagination from '../../../components/CustomTablePagination';
import PermissionButton from '../../../components/PermissionButton';
import ColumnFilter from '../../../components/ColumnFilter';
import { useColumnFilters } from '../../../hooks/useColumnFilters';
import { SlideUpTransition, useMinimizable, DialogControls, MinimizedBar } from '../../../components/MinimizableDialog';

const HR_COLOR = '#00897B';

const JF_FILTER_SCHEMA = {
  date:        { type: 'dateRange', paramFrom: 'date_from', paramTo: 'date_to' },
  nom:         { type: 'text', param: 'nom' },
  description: { type: 'text', param: 'description' },
} as const;

// Default fixed holidays for Morocco (dates are for a given year)
const getDefaultFixedHolidays = (year: number): JourFerieCreate[] => [
  { date: `${year}-01-01`, nom: "Jour de l'An", type: 'FIXE', description: '1er Janvier' },
  { date: `${year}-01-11`, nom: "Manifeste de l'Indépendance", type: 'FIXE', description: '11 Janvier' },
  { date: `${year}-05-01`, nom: 'Fête du Travail', type: 'FIXE', description: '1er Mai' },
  { date: `${year}-07-30`, nom: 'Fête du Trône', type: 'FIXE', description: '30 Juillet' },
  { date: `${year}-08-14`, nom: 'Allégeance Oued Eddahab', type: 'FIXE', description: '14 Août' },
  { date: `${year}-08-20`, nom: 'Révolution du Roi et du Peuple', type: 'FIXE', description: '20 Août' },
  { date: `${year}-08-21`, nom: 'Fête de la Jeunesse', type: 'FIXE', description: '21 Août' },
  { date: `${year}-11-06`, nom: 'Marche Verte', type: 'FIXE', description: '6 Novembre' },
  { date: `${year}-11-18`, nom: "Fête de l'Indépendance", type: 'FIXE', description: '18 Novembre' },
];

const JoursFeriesManagement: React.FC = () => {
  const [joursFeries, setJoursFeries] = useState<JourFerie[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [yearFilter, setYearFilter] = useState<number>(new Date().getFullYear());
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [availableYears, setAvailableYears] = useState<number[]>([]);

  // Dialog states
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const { minimized, setMinimized, minimize, restore, dialogOpen } = useMinimizable(formDialogOpen);
  const [selectedJourFerie, setSelectedJourFerie] = useState<JourFerie | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [jourFerieToDelete, setJourFerieToDelete] = useState<JourFerie | null>(null);
  const [initDialogOpen, setInitDialogOpen] = useState(false);

  // Form state
  const [formData, setFormData] = useState<JourFerieCreate>({
    date: '',
    nom: '',
    type: 'FIXE',
    description: '',
  });

  const { showSuccess, showError } = useAlert();
  const { filters, update, reset, hasActive, toQueryParams } = useColumnFilters(JF_FILTER_SCHEMA, {
    onChange: () => setPage(0),
  });

  const fetchJoursFeries = useCallback(async () => {
    setLoading(true);
    try {
      const extra = toQueryParams();
      const response = await joursFeriesAPI.getAll({
        page: page + 1,
        size: rowsPerPage,
        annee: yearFilter || undefined,
        type_jour: typeFilter || undefined,
        extra: Object.keys(extra).length > 0 ? extra : undefined,
      });
      setJoursFeries(response.items);
      setTotalCount(response.total);
    } catch (error: any) {
      showError(extractErrorMessage(error));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, yearFilter, typeFilter, showError, filters]);

  const fetchAvailableYears = useCallback(async () => {
    try {
      const years = await joursFeriesAPI.getYears();
      // Add current and next year if not present
      const currentYear = new Date().getFullYear();
      const allYears = new Set([...years, currentYear, currentYear + 1]);
      setAvailableYears(Array.from(allYears).sort((a, b) => b - a));
    } catch (error) {
      console.error('Error fetching years:', error);
      const currentYear = new Date().getFullYear();
      setAvailableYears([currentYear + 1, currentYear, currentYear - 1]);
    }
  }, []);

  useEffect(() => {
    fetchJoursFeries();
  }, [fetchJoursFeries]);

  useEffect(() => {
    fetchAvailableYears();
  }, [fetchAvailableYears]);

  const handlePageChange = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleYearFilterChange = (event: SelectChangeEvent<number>) => {
    setYearFilter(event.target.value as number);
    setPage(0);
  };

  const handleTypeFilterChange = (event: SelectChangeEvent) => {
    setTypeFilter(event.target.value);
    setPage(0);
  };

  const clearFilters = () => {
    setYearFilter(new Date().getFullYear());
    setTypeFilter('');
    reset();
    setPage(0);
  };

  // Cache an in-progress new jour férié so an accidental backdrop-close doesn't wipe it.
  const { saveDraft, loadDraft, clearDraft } = useFormDraft<{
    formData: JourFerieCreate;
  }>('jour-ferie');

  const handleAddJourFerie = () => {
    restore();
    setSelectedJourFerie(null);
    const draft = loadDraft();
    setFormData(
      draft?.formData ?? {
        date: '',
        nom: '',
        type: 'FIXE',
        description: '',
      }
    );
    setFormDialogOpen(true);
  };

  // Backdrop click / Escape = accidental: keep new-entry work as a draft, then close.
  const handleDialogClose = (
    _event: object,
    reason: 'backdropClick' | 'escapeKeyDown'
  ) => {
    if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') return;
    if (!selectedJourFerie) {
      saveDraft({ formData });
    }
    setFormDialogOpen(false);
  };

  // Annuler = deliberate cancel: discard any draft.
  const handleCancelForm = () => {
    clearDraft();
    setMinimized(false);
    setFormDialogOpen(false);
  };

  const handleEditJourFerie = (jourFerie: JourFerie) => {
    restore();
    setSelectedJourFerie(jourFerie);
    setFormData({
      date: jourFerie.date,
      nom: jourFerie.nom,
      type: jourFerie.type,
      description: jourFerie.description || '',
    });
    setFormDialogOpen(true);
  };

  const handleDeleteClick = (jourFerie: JourFerie) => {
    setJourFerieToDelete(jourFerie);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!jourFerieToDelete) return;

    try {
      await joursFeriesAPI.delete(jourFerieToDelete.id);
      showSuccess('Jour férié supprimé avec succès');
      fetchJoursFeries();
      fetchAvailableYears();
    } catch (error: any) {
      showError(extractErrorMessage(error));
    } finally {
      setDeleteDialogOpen(false);
      setJourFerieToDelete(null);
    }
  };

  const handleFormSubmit = async () => {
    if (!formData.date || !formData.nom) {
      showError('Veuillez remplir tous les champs obligatoires');
      return;
    }

    try {
      if (selectedJourFerie) {
        await joursFeriesAPI.update(selectedJourFerie.id, formData as JourFerieUpdate);
        showSuccess('Jour férié modifié avec succès');
      } else {
        await joursFeriesAPI.create(formData);
        showSuccess('Jour férié ajouté avec succès');
        clearDraft();
      }
      setFormDialogOpen(false);
      fetchJoursFeries();
      fetchAvailableYears();
    } catch (error: any) {
      showError(extractErrorMessage(error));
    }
  };

  const handleInitYear = async () => {
    try {
      const holidays = getDefaultFixedHolidays(yearFilter);
      const result = await joursFeriesAPI.createBulk(holidays);
      showSuccess(`${result.created.length} jours fériés créés. ${result.skipped.length} déjà existants.`);
      setInitDialogOpen(false);
      fetchJoursFeries();
      fetchAvailableYears();
    } catch (error: any) {
      showError(extractErrorMessage(error));
    }
  };

  const getTypeChip = (type: string) => {
    if (type === 'FIXE') {
      return (
        <Chip
          label="Fixe"
          size="small"
          sx={{ backgroundColor: HR_COLOR, color: 'white', fontWeight: 500 }}
        />
      );
    }
    return (
      <Chip
        label="Mobile"
        size="small"
        sx={{ backgroundColor: '#FF9800', color: 'white', fontWeight: 500 }}
      />
    );
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" sx={{ color: HR_COLOR, fontWeight: 600 }}>
          Gestion des Jours Fériés
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<EventIcon />}
            onClick={() => setInitDialogOpen(true)}
            sx={{
              borderColor: HR_COLOR,
              color: HR_COLOR,
              '&:hover': { borderColor: '#00695C', backgroundColor: 'rgba(0, 137, 123, 0.08)' },
            }}
          >
            Initialiser {yearFilter}
          </Button>
          <PermissionButton
            moduleName="HR"
            operation="add"
            miniModuleName="Jours Fériés"
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddJourFerie}
            sx={{
              backgroundColor: HR_COLOR,
              '&:hover': { backgroundColor: '#00695C' },
            }}
          >
            Ajouter
          </PermissionButton>
        </Box>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Année</InputLabel>
            <Select
              value={yearFilter}
              onChange={handleYearFilterChange}
              label="Année"
            >
              {availableYears.map((year) => (
                <MenuItem key={year} value={year}>
                  {year}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Type</InputLabel>
            <Select
              value={typeFilter}
              onChange={handleTypeFilterChange}
              label="Type"
            >
              <MenuItem value="">Tous</MenuItem>
              {TYPE_JOUR_FERIE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.value}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {(typeFilter || hasActive()) && (
            <Button
              variant="outlined"
              size="small"
              onClick={clearFilters}
              startIcon={<FilterListOffIcon />}
            >
              Effacer les filtres
            </Button>
          )}

          <Box sx={{ flexGrow: 1 }} />

          <Tooltip title="Actualiser">
            <IconButton onClick={fetchJoursFeries} sx={{ color: HR_COLOR }}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Paper>

      {/* Table */}
      <TableContainer component={Paper} sx={getModuleTableTheme('hr')}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Date
                  <ColumnFilter type="dateRange" value={filters.date} onChange={(v) => update('date', v)} label="date" color={HR_COLOR} />
                </Box>
              </TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Nom
                  <ColumnFilter type="text" value={filters.nom} onChange={(v) => update('nom', v)} label="nom" color={HR_COLOR} />
                </Box>
              </TableCell>
              <TableCell>Type</TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Description
                  <ColumnFilter type="text" value={filters.description} onChange={(v) => update('description', v)} label="description" color={HR_COLOR} />
                </Box>
              </TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                  Chargement...
                </TableCell>
              </TableRow>
            ) : joursFeries.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                  Aucun jour férié trouvé pour {yearFilter}
                </TableCell>
              </TableRow>
            ) : (
              joursFeries.map((jf) => (
                <TableRow key={jf.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      {formatDate(jf.date)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600} sx={{ color: HR_COLOR }}>
                      {jf.nom}
                    </Typography>
                  </TableCell>
                  <TableCell>{getTypeChip(jf.type)}</TableCell>
                  <TableCell>
                    <Typography variant="body2" color="textSecondary">
                      {jf.description || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                      <PermissionButton
                        moduleName="HR"
                        operation="edit"
                        miniModuleName="Jours Fériés"
                        component="IconButton"
                        size="small"
                        onClick={() => handleEditJourFerie(jf)}
                        sx={{ color: HR_COLOR }}
                        tooltip="Modifier"
                      >
                        <EditIcon fontSize="small" />
                      </PermissionButton>
                      <PermissionButton
                        moduleName="HR"
                        operation="delete"
                        miniModuleName="Jours Fériés"
                        component="IconButton"
                        size="small"
                        onClick={() => handleDeleteClick(jf)}
                        sx={{ color: '#F44336' }}
                        tooltip="Supprimer"
                      >
                        <DeleteIcon fontSize="small" />
                      </PermissionButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <CustomTablePagination
          count={totalCount}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
        />
      </TableContainer>

      {/* Form Dialog */}
      <Dialog open={dialogOpen} onClose={handleDialogClose} maxWidth="sm" fullWidth TransitionComponent={SlideUpTransition}>
        <DialogTitle sx={{ color: HR_COLOR, fontWeight: 600, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {selectedJourFerie ? 'Modifier le jour férié' : 'Ajouter un jour férié'}
          <DialogControls color="#009688" onMinimize={minimize} onClose={handleCancelForm} />
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                label="Date"
                type="date"
                fullWidth
                required
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Nom"
                fullWidth
                required
                value={formData.nom}
                onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                placeholder="Ex: Fête du Trône"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Type</InputLabel>
                <Select
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value as 'FIXE' | 'MOBILE' })}
                  label="Type"
                >
                  {TYPE_JOUR_FERIE_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Description"
                fullWidth
                multiline
                rows={2}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Description optionnelle"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCancelForm} color="inherit">
            Annuler
          </Button>
          <Button
            onClick={handleFormSubmit}
            variant="contained"
            sx={{ backgroundColor: HR_COLOR, '&:hover': { backgroundColor: '#00695C' } }}
          >
            {selectedJourFerie ? 'Modifier' : 'Ajouter'}
          </Button>
        </DialogActions>
      </Dialog>

      <MinimizedBar
        open={formDialogOpen && minimized}
        color="#009688"
        title={selectedJourFerie ? 'Modifier le jour férié' : 'Ajouter un jour férié'}
        onRestore={restore}
        onClose={handleCancelForm}
      />

      {/* Init Year Dialog */}
      <Dialog open={initDialogOpen} onClose={() => setInitDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: HR_COLOR, fontWeight: 600 }}>
          Initialiser les jours fériés fixes pour {yearFilter}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Cette action va créer les jours fériés fixes suivants pour l'année {yearFilter}:
          </Typography>
          <Box component="ul" sx={{ pl: 2 }}>
            {getDefaultFixedHolidays(yearFilter).map((h, i) => (
              <li key={i}>
                <Typography variant="body2">
                  <strong>{h.nom}</strong> - {new Date(h.date).toLocaleDateString('fr-FR')}
                </Typography>
              </li>
            ))}
          </Box>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
            Les jours fériés déjà existants ne seront pas dupliqués.
          </Typography>
          <Typography variant="body2" color="warning.main" sx={{ mt: 1 }}>
            Note: Les jours fériés mobiles (Aïd, etc.) doivent être ajoutés manuellement car leurs dates changent chaque année.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setInitDialogOpen(false)} color="inherit">
            Annuler
          </Button>
          <Button
            onClick={handleInitYear}
            variant="contained"
            sx={{ backgroundColor: HR_COLOR, '&:hover': { backgroundColor: '#00695C' } }}
          >
            Initialiser
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        open={deleteDialogOpen}
        type="warning"
        title="Confirmer la suppression"
        message={`Êtes-vous sûr de vouloir supprimer le jour férié "${jourFerieToDelete?.nom}" (${jourFerieToDelete?.date}) ?`}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteConfirm}
        showCancel
      />
    </Box>
  );
};

export default JoursFeriesManagement;
